import { useDebouncedCallback } from '@/lib/useDebouncedCallback';
import { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Returns a setter that merge-writes several URL search params at once, preserving every other param —
 * a falsy value deletes the key. History is replaced (not pushed).
 *
 * Use this over calling {@link useSetSearchParam} in sequence when the keys are related, e.g. a
 * cascading filter that must set one level and clear the ones below it: one write means one history
 * entry and no intermediate state where the child filter outlives its parent.
 */
export function useSetSearchParams(): (updates: Record<string, string | undefined>) => void {
    const [, setSearchParams] = useSearchParams();
    return useCallback(
        (updates) => {
            setSearchParams(
                (prev) => {
                    const next = new URLSearchParams(prev);
                    for (const [key, value] of Object.entries(updates)) {
                        if (value) next.set(key, value);
                        else next.delete(key);
                    }
                    return next;
                },
                { replace: true },
            );
        },
        [setSearchParams],
    );
}

/**
 * Returns a setter that merge-writes a single URL search param, preserving every other param — a falsy
 * value deletes the key. History is replaced (not pushed). Use this instead of `setSearchParams({ key })`,
 * which silently drops the other params.
 */
export function useSetSearchParam(): (key: string, value: string | undefined) => void {
    const setParams = useSetSearchParams();
    return useCallback((key, value) => setParams({ [key]: value }), [setParams]);
}

/**
 * A debounced text filter backed by a URL search param. The returned `input` stays instantly responsive;
 * the URL (the source of truth, so it survives a row-click → details → back round trip) updates after the
 * debounce. Seeded once from the current param value. Read the param directly for anything that must track
 * the URL synchronously (e.g. driving a query atom or an empty-state message).
 */
export function useDebouncedSearchParam(key: string): { input: string; onChange: (value: string) => void } {
    const [searchParams] = useSearchParams();
    const setParam = useSetSearchParam();
    const [input, setInput] = useState(searchParams.get(key) ?? '');
    const applyDebounced = useDebouncedCallback((value: string) => setParam(key, value.trim() || undefined));
    const onChange = useCallback(
        (value: string) => {
            setInput(value);
            applyDebounced(value);
        },
        [applyDebounced],
    );
    return { input, onChange };
}
